import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Archive, Zap, Trash2 } from 'lucide-react';
import { CharacterData, ComputedStats } from '../../types/character';
import { getDomainCardsForDisplay, ResolvedDomainCardWithCharacterData } from '../../utils/contentResolver';
import { useVaultLoadout } from '../../hooks/useVaultLoadout';
import DomainCard from './DomainCard';
import './DomainCard.css';
import './DomainCardManager.css';

interface DomainCardManagerProps {
  enhancedCharacter: {
    character: CharacterData | null;
    computedStats: ComputedStats | null;
    addDomainCardToLoadout?: (cardId: string, autoVault?: boolean) => void;
    moveDomainCardToVault?: (cardId: string) => void;
    moveDomainCardToLoadout?: (cardId: string) => void;
    removeDomainCard?: (cardId: string) => void;
  };
}

interface EnhancedDomainCard extends ResolvedDomainCardWithCharacterData {
  location: 'loadout' | 'vault';
  isActive: boolean;
}

const DomainCardManager: React.FC<DomainCardManagerProps> = ({ enhancedCharacter }) => {
  const [domainCards, setDomainCards] = useState<EnhancedDomainCard[]>([]);
  const [loading, setLoading] = useState(false);
  
  const vaultLoadout = useVaultLoadout(enhancedCharacter.character, enhancedCharacter);

  useEffect(() => {
    const loadDomainCards = async () => {
      if (!enhancedCharacter.character) {
        setDomainCards([]);
        return;
      }

      setLoading(true);
      try {
        const resolvedCards = await getDomainCardsForDisplay(enhancedCharacter.character);
        const loadout = enhancedCharacter.character.inventory?.domainCardLoadout || [];
        
        // Enhance cards with location and status information
        const enhancedCards: EnhancedDomainCard[] = resolvedCards.map(card => {
          const inLoadout = loadout.some(ref => ref.contentId === card.id);
          const location: 'loadout' | 'vault' = inLoadout ? 'loadout' : 'vault';
          
          return {
            ...card,
            location,
            isActive: location === 'loadout' // Only loadout cards affect stats
          };
        });
        
        // Sort by location (loadout first) then by level (lowest first)
        enhancedCards.sort((a, b) => {
          if (a.location !== b.location) {
            return a.location === 'loadout' ? -1 : 1;
          }
          
          // Within same location, sort by level (lowest first)
          const levelA = (a as any).level || 0;
          const levelB = (b as any).level || 0;
          
          if (levelA !== levelB) {
            return levelA - levelB;
          }
          
          // If levels are the same, fall back to name sorting
          return a.name.localeCompare(b.name);
        });
        
        setDomainCards(enhancedCards);
      } catch (error) {
        console.error('Failed to load domain cards:', error);
        setDomainCards([]);
      } finally {
        setLoading(false);
      }
    };

    loadDomainCards();
  }, [enhancedCharacter.character?.inventory?.domainCardLoadout, enhancedCharacter.character?.inventory?.domainCardVault]);

  // Don't render the component if loading or no cards
  if (loading || domainCards.length === 0) {
    return null;
  }

  // Count cards by location and get loadout info
  const loadoutCards = domainCards.filter(card => card.location === 'loadout');
  const vaultCards = domainCards.filter(card => card.location === 'vault');
  const state = vaultLoadout.analyzeState();

  const handleMoveToVault = (cardId: string) => {
    console.log('Moving card to vault:', cardId);
    if (enhancedCharacter.moveDomainCardToVault) {
      enhancedCharacter.moveDomainCardToVault(cardId);
      console.log('Successfully called moveDomainCardToVault');
    } else {
      console.error('moveDomainCardToVault method not available');
    }
  };

  const handleMoveToLoadout = (cardId: string) => {
    console.log('Moving card to loadout:', cardId);
    if (enhancedCharacter.moveDomainCardToLoadout) {
      enhancedCharacter.moveDomainCardToLoadout(cardId);
      console.log('Successfully called moveDomainCardToLoadout');
    } else {
      console.error('moveDomainCardToLoadout method not available');
    }
  };

  const handleRemoveCard = (cardId: string) => {
    console.log('Removing card completely:', cardId);
    if (enhancedCharacter.removeDomainCard) {
      enhancedCharacter.removeDomainCard(cardId);
      console.log('Successfully called removeDomainCard');
    } else {
      console.error('removeDomainCard method not available');
    }
  };

  return (
    <motion.div
      className="domain-card-manager"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="domain-card-manager-header"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <motion.h3
          className="domain-card-manager-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Domain Cards ({domainCards.length})
          <span className="domain-card-manager-stats">
            {' '}• Loadout: {loadoutCards.length}/5 • Vault: {vaultCards.length}
          </span>
        </motion.h3>
      </motion.div>

      <motion.div 
        className="domain-cards-grid"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, staggerChildren: 0.1 }}
      >
        <AnimatePresence mode="popLayout">
          {domainCards.map((card, index) => (
            <motion.div
              key={card.id}
              className="domain-card-item"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{
                opacity: 0,
                scale: 0.1,
                transition: { duration: 0.3 }
              }}
              transition={{
                type: "spring",
                stiffness: 300,
                damping: 25,
                delay: index * 0.05
              }}
              layout
            >

              <div className="domain-card-wrapper">
                <DomainCard
                  item={{
                    ...card,
                    description: card.description || '', // Ensure description is never undefined
                    modifiers: card.modifiers || []      // Ensure modifiers is never undefined
                  }}
                  defaultExpanded={card.location === 'loadout'} // Smart defaults: loadout expanded, vault collapsed
                />
                
                {/* Action buttons as a footer bar */}
                <div className="domain-card-actions">
                  {card.location === 'loadout' ? (
                    <button
                      onClick={() => handleMoveToVault(card.id)}
                      className="domain-card-button"
                    >
                      <Archive size={14} className="domain-card-button-icon" />
                      Vault
                    </button>
                  ) : (
                    <button
                      onClick={() => handleMoveToLoadout(card.id)}
                      disabled={state.loadoutCount >= 5}
                      className={`domain-card-button ${
                        state.loadoutCount >= 5
                          ? 'domain-card-button--disabled'
                          : 'domain-card-button--success'
                      }`}
                    >
                      <Zap size={14} className="domain-card-button-icon" />
                      To Loadout
                    </button>
                  )}
                  <button
                    onClick={() => handleRemoveCard(card.id)}
                    className="domain-card-button domain-card-button--danger"
                  >
                    <Trash2 size={14} className="domain-card-button-icon" />
                    Remove
                  </button>
                </div>
              </div>

            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default DomainCardManager;